import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import "./AddUser.css";
import AuthService from "../../services/Auth.service";
import { Eye, EyeOff } from "lucide-react";
import Alert from "../Alert/Alert"; // import your Alert component

// Validation schema
const schema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
  verify_password: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords do not match")
    .required("Please confirm your password"),
  role: Yup.string().required("Role is required"),
});

const AddUser = () => {
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showVerifyPassword, setShowVerifyPassword] = useState(false);

  const [alertValue, setAlertValue] = useState({
    show: false,
    message: "",
    variant: "success",
    position: "middle",
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      verify_password: "",
      role: "",
    },
  });

  // Fetch roles
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await AuthService.getUserRoles();
        if (response?.data?.user_roles) {
          setRoles(response.data.user_roles);
        }
      } catch (error) {
        console.error("Error fetching roles:", error);
      } finally {
        setLoadingRoles(false);
      }
    };
    fetchRoles();
  }, []);

  const onSubmit = async (data) => {
    try {
      const response = await AuthService.createUser(data);
      setAlertValue({
        show: true,
        message: response.data.message,
        variant: "success",
        position: "middle",
      });
    } catch (error) {
      console.error("Error creating user:", error);
      setAlertValue({
        show: true,
        message: error.response?.data?.detail || "Failed to create user",
        variant: "",
        position: "middle",
      });
    }
  };

  return (
    <div className="add-user-container">
      <h2 className="form-title">Add User</h2>

      {alertValue.show && (
        <Alert
          variant={alertValue.variant}
          message={alertValue.message}
          dismissible={true}
          timeout={2500}
          position={alertValue.position}
          onClose={() =>
            setAlertValue({ show: false, message: "", variant: "" })
          }
        />
      )}

      <form className="add-user-form" onSubmit={handleSubmit(onSubmit)}>
        <div className="form-row">
          {/* Name */}
          <div className="form-group">
            <label>Name</label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <input {...field} type="text" placeholder="Enter name" />
              )}
            />
            {errors.name && (
              <span className="error-msg">{errors.name.message}</span>
            )}
          </div>

          {/* Email */}
          <div className="form-group">
            <label>Email</label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <input {...field} type="email" placeholder="Enter email" />
              )}
            />
            {errors.email && (
              <span className="error-msg">{errors.email.message}</span>
            )}
          </div>
        </div>

        <div className="form-row">
          {/* Password */}
          <div className="form-group password-group">
            <label>Password</label>
            <div className="password-input-wrapper">
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                  />
                )}
              />
              <span
                className="password-toggle-icon"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </span>
            </div>
            {errors.password && (
              <span className="error-msg">{errors.password.message}</span>
            )}
          </div>

          {/* Verify Password */}
          <div className="form-group password-group">
            <label>Verify Password</label>
            <div className="password-input-wrapper">
              <Controller
                name="verify_password"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type={showVerifyPassword ? "text" : "password"}
                    placeholder="Confirm password"
                  />
                )}
              />
              <span
                className="password-toggle-icon"
                onClick={() => setShowVerifyPassword((prev) => !prev)}
              >
                {showVerifyPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </span>
            </div>
            {errors.verify_password && (
              <span className="error-msg">
                {errors.verify_password.message}
              </span>
            )}
          </div>
        </div>

        <div className="form-row">
          {/* Role */}
          <div className="form-group">
            <label>Role</label>
            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <select {...field} disabled={loadingRoles}>
                  <option value="">Select a role</option>
                  {roles.map((role, index) => (
                    <option key={index} value={role}>
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </option>
                  ))}
                </select>
              )}
            />
            {errors.role && (
              <span className="error-msg">{errors.role.message}</span>
            )}
          </div>
        </div>

        <button type="submit" className="submit-btn">
          Add User
        </button>
      </form>
    </div>
  );
};

export default AddUser;
